<script lang="ts">
  import { t } from "./i18n.svelte";
  import "uplot/dist/uPlot.min.css";
  import { untrack } from "svelte";
  import uPlot, { type AlignedData, type Options } from "uplot";

  import { subscribeBgSettings } from "./bg-settings";
  import { REIHEN_FOLGE } from "./farbe/farbe";

  interface Props {
    /** xs (unix-seconds) and one or more y-series, all aligned. */
    data: AlignedData;
    /** Labels for the y-series in render order. */
    labels: string[];
    /** Y-axis unit suffix (rendered next to values). */
    unit?: string;
    /** Pixel height of the chart container. */
    height?: number;
    /**
     * Platz je Serie in der Diagramm-Palette (1-8, --reihe-N), am besten
     * über REIHE aus lib/farbe/farbe.ts: die Farbe folgt der Messgröße.
     * Gilt bei „Diagramme: bunt" (Einstellungen › Farbe); bei „grau" und
     * „Bereichston" zeichnet das Diagramm Abstufungen einer Farbe in der
     * Reihenfolge der Serien. Zustandsfarben sind KEINE Serienfarben --
     * bis 03.10.2026 war Temperatur rot und CO₂ gelb.
     */
    plaetze?: number[];
    /** Serien (Index in `labels`), die immer leise grau bleiben, etwa
     *  die Ränder eines Korridors. */
    leise?: number[];
    /**
     * Datenpunkt-Marker auf der Linie sichtbar erzwingen. Default
     * (false / unset) → uPlot entscheidet via Density-Heuristik.
     */
    showPoints?: boolean;
    /**
     * Korridor zwischen zwei Serien (Indizes in `labels`, 0-basiert),
     * z. B. Tief/Hoch. Die Fläche wird in der Farbe der oberen Serie
     * transparent gefüllt, beide Randlinien werden dünner, damit die
     * übrigen Serien vorne bleiben.
     */
    band?: { upper: number; lower: number };
    /**
     * Nachkommastellen in Legende/Tooltip und Ø-Label. Default 1 passt
     * für °C/%/W; Netzfrequenz braucht 3, sonst steht dort nur "50.0 Hz".
     */
    decimals?: number;
    /**
     * Zeitabschnitte (unix-seconds, von–bis), die hinter den Kurven
     * hinterlegt werden: ein Zustand, der in dieser Zeit galt, etwa „hier
     * meldete der Melder Bewegung". Neutral grau -- ein Abschnitt ist
     * keine Wertung, und die Zustandsfarben gehören nicht ins Diagramm.
     */
    zonen?: [number, number][];
    /**
     * Legende unter dem Diagramm (Namen und Werte unter dem Zeiger).
     * Aus, wenn die Karte die Werte schon zeigt (Dashboard „Lüftung &
     * Heizung": sie wiederholte die drei Zahlen und kostete am Telefon
     * zwei Zeilen).
     */
    legende?: boolean;
  }

  let {
    data,
    labels,
    unit = "",
    legende = true,
    height = 280,
    plaetze,
    leise,
    showPoints = false,
    band,
    decimals = 1,
    zonen,
  }: Props = $props();

  // Beliebige CSS-Farbe (auch oklch/Variablen-Werte) als rgba mit Alpha.
  // Über einen 1x1-Canvas statt String-Parsing: der Browser normalisiert,
  // und Canvas-Fills mit oklch sind in Safari nicht verlässlich.
  function withAlpha(color: string, alpha: number): string {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    const ctx = c.getContext("2d");
    if (!ctx) return color;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  let container = $state<HTMLDivElement | undefined>(undefined);
  let chart: uPlot | undefined;
  let resizeObserver: ResizeObserver | undefined;

  // Read theme colors from the live document so the chart re-themes
  // when the user toggles dark mode (der Bau-Effekt unten hängt an
  // `dunkel` und liest sie dann neu).
  function themeColors(): { stroke: string; grid: string; axis: string } {
    const cs = getComputedStyle(document.documentElement);
    return {
      stroke: cs.getPropertyValue("--accent").trim() || "#3b82f6",
      grid: cs.getPropertyValue("--border").trim() || "#e5e7eb",
      axis: cs.getPropertyValue("--fg-muted").trim() || "#6b7280",
    };
  }

  // Series-color rotation — extend if a chart ever needs more.
  // Farbweise aus den Einstellungen. Ein Wechsel baut das Diagramm neu
  // (die Farben stehen in den uPlot-Optionen, nicht im CSS).
  let farbStand = $state("");
  $effect(() =>
    subscribeBgSettings((s) => {
      farbStand = `${s.farbe.diagramme}|${s.farbe.kraft}|${JSON.stringify(s.farbe.bereiche)}|${s.accentHue}`;
    }),
  );

  // Hell/Dunkel: Gitter, Achsen und Serien stehen als feste Farben in den
  // uPlot-Optionen, ein Wechsel braucht also einen Neubau. Das hing bis
  // 10.10.2026 daran, dass ohnehin jeder neue Wert neu baute.
  //
  // Beobachtet wird die Klasse am Dokument, nicht der Theme-Store: der
  // Store kippt, BEVOR die Klasse gesetzt ist, und ein Neubau in dem
  // Moment liest noch die alten Farben (so gemessen: helles Gitter auf
  // dunklem Grund). Der Beobachter meldet sich erst nach der Änderung.
  let dunkel = $state(document.documentElement.classList.contains("dark"));
  $effect(() => {
    const mo = new MutationObserver(() => {
      dunkel = document.documentElement.classList.contains("dark");
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  });

  // Löst einen CSS-Farbausdruck AM ORT des Diagramms auf (dort gelten die
  // Farben seines Bereichs) und gibt ihn dem Canvas als rgb().
  function aufgeloest(ausdruck: string, fallback: string): string {
    if (!container) return fallback;
    const probe = document.createElement("span");
    probe.style.cssText = `position:absolute;visibility:hidden;color:${ausdruck}`;
    container.appendChild(probe);
    const farbe = getComputedStyle(probe).color;
    probe.remove();
    return farbe ? withAlpha(farbe, 1) : fallback;
  }

  // Tick-Format fuer die X-Achse abhaengig vom Schrittweite-incr:
  // <Stunde -> "HH:mm", <Tag -> "HH:mm", >=Tag -> "TT.MM.", >=Jahr ->
  // "MM.YYYY". Lokale "de-DE" damit Punktnotation und 24h-Stunden
  // konsistent zur restlichen App durchziehen (memory: format.ts).
  function formatTick(unixSec: number, incr: number): string {
    const d = new Date(unixSec * 1000);
    if (incr >= 365 * 86400) {
      return d.toLocaleDateString("de-DE", {
        month: "2-digit",
        year: "numeric",
      });
    }
    if (incr >= 86400) {
      return d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
      });
    }
    // Unter einer Minute Schrittweite mit Sekunden: ein Fenster von zwei
    // Minuten zeigte sonst viermal dieselbe Uhrzeit nebeneinander.
    return d.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      ...(incr < 60 ? { second: "2-digit" as const } : {}),
      hour12: false,
    });
  }

  function formatCursor(unixSec: number): string {
    return new Date(unixSec * 1000).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  // Lineare Regression (kleinste Quadrate) ueber die erste y-Serie.
  // Liefert Steigung/Achsenabschnitt in Daten-Koordinaten (x = unix-sec)
  // oder null, wenn zu wenige gueltige Punkte vorliegen.
  function leastSquares(
    xs: ArrayLike<number | null | undefined>,
    ys: ArrayLike<number | null | undefined>,
  ): { m: number; b: number } | null {
    let n = 0;
    let sx = 0;
    let sy = 0;
    let sxx = 0;
    let sxy = 0;
    for (let i = 0; i < xs.length; i++) {
      const x = xs[i];
      const y = ys[i];
      if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y))
        continue;
      n += 1;
      sx += x;
      sy += y;
      sxx += x * x;
      sxy += x * y;
    }
    if (n < 2) return null;
    const denom = n * sxx - sx * sx;
    if (denom === 0) return null;
    const m = (n * sxy - sx * sy) / denom;
    const b = (sy - m * sx) / n;
    return { m, b };
  }

  // Zeichnet eine gestrichelte Trendlinie (lineare Regression) ueber das
  // angezeigte Intervall der ersten y-Serie. Liest die aktuellen Daten bei
  // jedem draw -- folgt also dem TimeRangePicker ohne Chart-Neubau. Nur bei
  // genau einer y-Serie aktiv (sonst mehrdeutig).
  function trendLinePlugin(): uPlot.Plugin {
    return {
      hooks: {
        draw: (u: uPlot) => {
          if (u.series.length !== 2) return;
          const xs = u.data[0];
          const ys = u.data[1];
          if (!xs || !ys || xs.length < 2) return;
          const fit = leastSquares(xs, ys);
          if (!fit) return;
          const x0 = xs[0];
          const x1 = xs[xs.length - 1];
          if (x0 == null || x1 == null) return;
          const color = seriesColor(0, "#3b82f6");
          const dpr = uPlot.pxRatio || 1;
          const ctx = u.ctx;
          ctx.save();
          // Auf die Plot-Flaeche clippen, damit die Linie bei gezoomtem
          // x-Bereich nicht in die Achsen laeuft.
          ctx.beginPath();
          ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
          ctx.clip();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5 * dpr;
          ctx.setLineDash([6 * dpr, 4 * dpr]);
          ctx.beginPath();
          ctx.moveTo(u.valToPos(x0, "x", true), u.valToPos(fit.m * x0 + fit.b, "y", true));
          ctx.lineTo(u.valToPos(x1, "x", true), u.valToPos(fit.m * x1 + fit.b, "y", true));
          ctx.stroke();
          ctx.restore();
        },
      },
    };
  }

  // Hinterlegt die Abschnitte aus `zonen`, bevor Achsen und Kurven
  // gezeichnet werden. Liest die Abschnitte bei jedem Zeichnen frisch --
  // sie wachsen mit den Daten, ohne dass das Diagramm neu gebaut wird.
  let zonenFarbe = "rgba(128, 128, 128, 0.12)";
  function zonenPlugin(): uPlot.Plugin {
    return {
      hooks: {
        drawClear: (u: uPlot) => {
          if (!zonen?.length) return;
          const dpr = uPlot.pxRatio || 1;
          const ctx = u.ctx;
          ctx.save();
          ctx.beginPath();
          ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
          ctx.clip();
          ctx.fillStyle = zonenFarbe;
          for (const [von, bis] of zonen) {
            const x0 = u.valToPos(von, "x", true);
            const x1 = u.valToPos(bis, "x", true);
            // Auch ein Abschnitt von einer Sekunde bleibt sichtbar.
            ctx.fillRect(x0, u.bbox.top, Math.max(x1 - x0, 2 * dpr), u.bbox.height);
          }
          ctx.restore();
        },
      },
    };
  }

  // Durchschnitt der ersten y-Serie ueber das angezeigte Intervall, als
  // Text fuer die Legenden-Ecke unten rechts. Nur bei genau einer Serie.
  const avgLabel = $derived.by(() => {
    if (labels.length !== 1) return null;
    const ys = data[1];
    if (!ys) return null;
    let sum = 0;
    let n = 0;
    for (const v of ys) {
      if (v != null && Number.isFinite(v)) {
        sum += v;
        n += 1;
      }
    }
    if (n === 0) return null;
    return `Ø ${komma((sum / n).toFixed(decimals))}${unit}`;
  });

  // Deutsches Komma (docs/gestaltung.md): „49,998 Hz", nicht „49.998 Hz" --
  // in Achse, Legende und Ø. Bis 01.10.2026 schrieb das Diagramm überall
  // den Dezimalpunkt.
  const komma = (s: string): string => s.replace(".", ",");

  function seriesColor(i: number, fallback: string): string {
    if (leise?.includes(i)) return aufgeloest("var(--fg-subtle)", fallback);
    if (document.documentElement.dataset.farbeDiagramme === "bunt") {
      const platz = plaetze?.[i] ?? REIHEN_FOLGE[i % REIHEN_FOLGE.length];
      return aufgeloest(`var(--reihe-${platz})`, fallback);
    }
    // Stufe nach der Reihenfolge der NICHT leisen Serien: die erste
    // sichtbare Reihe ist immer die kräftigste.
    const rang = labels.slice(0, i).filter((_, k) => !leise?.includes(k)).length;
    return aufgeloest(`var(--stufe-${(rang % 5) + 1})`, fallback);
  }

  function buildOptions(width: number): Options {
    const theme = themeColors();
    const inBand = (i: number) => band != null && (i === band.upper || i === band.lower);
    zonenFarbe = withAlpha(aufgeloest("var(--fg)", theme.axis), 0.1);
    return {
      width,
      height,
      plugins: [zonenPlugin(), trendLinePlugin()],
      // uPlot-Serienindex = labels-Index + 1 (Index 0 ist die x-Achse).
      ...(band
        ? {
            bands: [
              {
                series: [band.upper + 1, band.lower + 1] as [number, number],
                fill: withAlpha(seriesColor(band.upper, theme.stroke), 0.14),
              },
            ],
          }
        : {}),
      legend: { show: legende },
      cursor: {
        drag: { x: true, y: false },
        sync: undefined,
      },
      scales: {
        x: { time: true },
      },
      axes: [
        {
          stroke: theme.axis,
          grid: { stroke: theme.grid, width: 1 },
          ticks: { stroke: theme.grid, width: 1 },
          values: (_u, splits, _ax, _fs, foundIncr) =>
            splits.map((s) => formatTick(s, foundIncr)),
        },
        {
          stroke: theme.axis,
          grid: { stroke: theme.grid, width: 1 },
          ticks: { stroke: theme.grid, width: 1 },
          values: (_u, vs) => vs.map((v) => (v == null ? "" : komma(String(v)) + unit)),
          // uPlots Default-Achsenbreite (~50 px) schneidet laengere Labels
          // ab -- z.B. "1015 hPa" auf der Druck-Skala. Hier messen wir die
          // breiteste Label-Breite und reservieren Platz inkl. Tick-Pad.
          size: (self, values) => {
            const FALLBACK = 50;
            if (!values || values.length === 0) return FALLBACK;
            const ctx = self.ctx;
            if (!ctx) return FALLBACK;
            const longest = values.reduce(
              (acc, s) => (s && s.length > acc.length ? s : acc),
              "",
            );
            if (!longest) return FALLBACK;
            const prev = ctx.font;
            // uPlot's axis-font; greife auf den computed Stil zurueck,
            // wenn der Canvas-Kontext noch keinen gesetzt hat.
            if (!ctx.font || ctx.font.startsWith("10px")) {
              ctx.font = "12px sans-serif";
            }
            const w = Math.ceil(ctx.measureText(longest).width);
            ctx.font = prev;
            // 14 px Puffer = Tick-Strich + Label-Padding (uPlot-default).
            return Math.max(FALLBACK, w + 14);
          },
        },
      ],
      series: [
        {
          // Ohne Namen schreibt uPlot „Time" in die Legende (DNS-Seite
          // 24.10.2026, galt für jedes Diagramm).
          label: t("chart.zeit", "Zeit"),
          value: (_u, ts) => (ts == null ? "" : formatCursor(ts)),
        },
        ...labels.map((label, i) => {
          const color = seriesColor(i, theme.stroke);
          return {
            label,
            stroke: color,
            width: inBand(i) ? 1 : 2,
            spanGaps: false,
            // showPoints=false (default) lässt uPlot via Density-
            // Heuristik selber entscheiden (sieht meistens am besten
            // aus). Caller, die Datenpunkte zwingend wollen, setzen
            // showPoints={true}.
            ...(showPoints ? { points: { show: true, size: 4 } } : {}),
            value: (_u: uPlot, raw: number | null) =>
              raw == null ? "—" : komma(raw.toFixed(decimals)) + unit,
          };
        }),
      ],
    };
  }

  $effect(() => {
    if (!container) return;
    void farbStand; // Farbweise geändert -> neu bauen
    void dunkel; // Hell/Dunkel gewechselt -> neu bauen

    // Die Daten hier NICHT als Abhängigkeit lesen: neue Werte reicht der
    // Effekt unten per setData() nach. Bis 10.10.2026 baute jeder neue
    // Messwert das ganze Diagramm neu -- bei einem Verlauf, der einmal je
    // Minute nachlädt, fiel das nicht auf; bei einer Kurve im Sekundentakt
    // (Funkmelder) verschwand dabei jede Sekunde der Zeiger samt Legende.
    chart = new uPlot(buildOptions(container.clientWidth), untrack(() => data), container);

    resizeObserver = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0 && chart) chart.setSize({ width: w, height });
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver?.disconnect();
      chart?.destroy();
      chart = undefined;
    };
  });

  // Re-feed data when the prop changes without rebuilding the chart.
  // Die Trendlinie wird vom plugin-`draw`-Hook bei jedem Zeichnen frisch
  // berechnet -- setData() loest ein Neuzeichnen aus, daher folgt die Linie
  // den Daten ohne eigenen redraw-Effekt.
  $effect(() => {
    void zonen; // Abschnitte geändert -> neu zeichnen, auch ohne neue Werte
    if (chart) chart.setData(data);
  });
</script>

<div class="relative w-full">
  <div bind:this={container} class="w-full overflow-hidden"></div>
  {#if avgLabel}
    <div
      class="text-fg-subtle pointer-events-none absolute right-1 bottom-0 t-eyebrow tabular-nums"
    >
      {avgLabel}
    </div>
  {/if}
</div>

<style>
  /* uPlot default css uses HTML colors; align them with our tokens. */
  :global(.uplot .u-legend) {
    color: var(--fg-muted);
    font-size: 0.75rem;
  }
  :global(.uplot .u-title) {
    color: var(--fg);
  }
</style>
